"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Smile, Frown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordSchema } from "@/utils/schema/forgotPasswordSchema";
import { useForgotPasswordMutation } from "@/store/slices/apiSlice";
import { SHOW_MESSAGE } from "@/lib/constant/constant";
import { MessageIcon, Theme_logo } from "@/lib/Images";
import { cn } from "@/lib/utils";
import { useAdminForgotPasswordMutation } from "@/store/slices/apiSlice";
const ForgotPassword = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const [forgotPassword, { isLoading }] = useAdminForgotPasswordMutation();
  const [alertMsg, setAlertMsg] = useState({
    open: false,
    label: "",
    msg: "",
    isError: false,
    isLoading: false,
    btnText: "",
    canClose: false,
  });

  const form = useForm({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
    mode: "onChange",
  });

  const handleSubmit = async (data) => {
    try {
      // const res = await forgotPassword(data).unwrap();
      const payload = {
        email: data.email,
      };

      const res = await forgotPassword(payload).unwrap();
      if (res?.response.statusEnum === 1) {
        setSubmittedEmail(data.email);
        setIsSubmitted(true);
      } else {
        if (res?.response.errorCode === 164) {
          res?.response.data.sets.forEach((err) => {
            setAlertMsg({
              open: true,
              label: SHOW_MESSAGE?.VALIDATION_FAILED,
              msg: err?.message || res?.response.message,
              isError: true,
              btnText: "Close",
              canClose: true,
            });
          });
        } else {
          setAlertMsg({
            open: true,
            label: SHOW_MESSAGE?.FORGOT_PASSWORD_FAILED,
            msg: res?.response.message,
            isError: true,
            btnText: "Close",
            canClose: true,
          });
        }
      }
    } catch (err) {
      setAlertMsg({
        open: true,
        label: SHOW_MESSAGE?.FORGOT_PASSWORD_FAILED,
        msg: SHOW_MESSAGE?.SOMETHING_WENT_WRONG,
        isError: true,
        btnText: "Close",
        canClose: true,
      });
      console.error("Forgot Password Error:", err);
    }
  };

  if (isSubmitted) {
    return (
      <div className="w-full max-w-3xl px-4">
        <div className="text-center mb-10">
          <div className="flex justify-center">
            {/* <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center"> */}
            <img
              src={`${MessageIcon}`} // 👈 replace with your image path
              alt="icon"
              className="h-56 w-96 object-contain"
            />
            {/* </div> */}
          </div>

          <h1 className="text-xl font-bold text-foreground mb-4">
            Reset password link has been sent to your email Id
          </h1>

          <p className="text-responsive-sm text-foreground/80 max-w-md mx-auto">
            We've sent a reset password link to your {submittedEmail} email
            address. Head to your inbox, click the link and reset your password.
          </p>
        </div>

        <div className="space-y-4 max-w-md mx-auto">
          {/* <Button
            type="button"
            className="w-full h-12"
            onClick={() => {
              setIsSubmitted(false);
              form.reset();
            }}
          >
            Got it!
          </Button> */}

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm font-semibold text-[#1E9BFF] hover:underline"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl px-4">
      <div className="text-center mb-10">
        <div className="flex justify-center mb-16">
          <img
            src={Theme_logo || "/placeholder.svg"}
            className="h-20"
            alt="Simstac Systems Logo"
          />
        </div>

        <h1 className="text-2xl font-bold text-foreground mb-4">
          Forgot Password
        </h1>

        <p className="text-responsive-sm text-foreground/80">
          No worries! Enter your registered email to receive the password reset
          link.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-6 max-w-md mx-auto"
          noValidate
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="sr-only" htmlFor="email">
                  Email address
                </FormLabel>

                <FormControl>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      error={fieldState.error}
                      aria-invalid={fieldState.error ? "true" : "false"}
                      className={cn(
                        "w-full h-12 pr-11 bg-white text-black",
                        fieldState.error
                          ? "border-red-500 focus-visible:ring-red-500"
                          : field.value
                            ? "border-green-500 focus-visible:ring-green-500"
                            : "",
                      )}
                      {...field}
                    />

                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {fieldState.error ? (
                        <Frown className="h-5 w-5 text-red-500" />
                      ) : field.value ? (
                        <Smile className="h-5 w-5 text-green-500" />
                      ) : null}
                    </div>
                  </div>
                </FormControl>

                <FormMessage className="text-red-500 text-sm mt-1" />
              </FormItem>
            )}
          />

          {alertMsg?.isError && (
            <div className="p-3 text-responsive-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-md">
              {alertMsg.label && (
                <strong className="block mb-1">{alertMsg.label}</strong>
              )}
              {alertMsg.msg}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-12 !mt-10"
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send Reset Link"}
          </Button>

          <div className="text-center">
            <Link
              to="/login"
              className="text-sm font-semibold text-[#1E9BFF] hover:underline"
            >
              Back to Login
            </Link>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ForgotPassword;
